import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import {
  captureError,
  setMonitoringUser,
  trackEvent,
} from "../services/monitoring";
import type { User } from "../types/firestore";

export type AdminAuthState = {
  user: FirebaseUser | null;
  userDoc: User | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
};

type AdminAuthContextType = AdminAuthState & {
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
};

const defaultState: AdminAuthState = {
  user: null,
  userDoc: null,
  isAdmin: false,
  loading: true,
  error: null,
};

export const AdminAuthContext = createContext<AdminAuthContextType>({
  ...defaultState,
  signInWithGoogle: async () => {},
  signOut: async () => {},
  clearError: () => {},
});

export function useAdminAuth(): AdminAuthContextType {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
}

type AdminAuthProviderProps = {
  children: ReactNode;
};

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userDoc, setUserDoc] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestUidRef = useRef<string | null>(null);

  const isAdmin = Boolean(userDoc?.roles?.admin);

  const fetchUserDoc = useCallback(
    async (uid: string): Promise<User | null> => {
      try {
        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          return { uid: snap.id, ...snap.data() } as User;
        }
      } catch (err) {
        captureError(err, {
          scope: "admin_auth",
          operation: "fetch_user_doc",
        });
        console.error("[AdminAuth] Error fetching user doc:", err);
      }
      return null;
    },
    [],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const uid = firebaseUser?.uid ?? null;
      latestUidRef.current = uid;
      setUser(firebaseUser);
      setMonitoringUser(
        firebaseUser
          ? {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
            }
          : null,
      );
      if (firebaseUser) {
        const docData = await fetchUserDoc(firebaseUser.uid);
        if (latestUidRef.current === firebaseUser.uid) {
          setUserDoc(docData);
        }
      } else {
        setUserDoc(null);
      }
      if (latestUidRef.current === uid) {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [fetchUserDoc]);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      trackEvent("admin_sign_in_started", { provider: "google" });
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Sign-in failed. Try again.";
      captureError(err, {
        scope: "admin_auth",
        operation: "sign_in",
      });
      setError(message);
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      trackEvent("admin_sign_out_started");
      await firebaseSignOut(auth);
    } catch (err) {
      captureError(err, {
        scope: "admin_auth",
        operation: "sign_out",
      });
      console.error("[AdminAuth] Sign out error:", err);
      setError("Sign out failed.");
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value: AdminAuthContextType = {
    user,
    userDoc,
    isAdmin,
    loading,
    error,
    signInWithGoogle,
    signOut,
    clearError,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}
